import { Box, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitList } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitAvatar,
    KitChip,
    KitChips,
    KitEmpty,
    KitField,
    KitFab,
    KitSearch,
    KitSelect,
    KitStickyToolbar,
    KitToolbarButton,
} from '../common/MobileKitExtras';
import { APP_BAR_OFFSET } from '../layout/mobileShell';
import { avatarTone, customerInitials, customerSubtitle } from './customerMobileUtils';

/**
 * Lista de clientes no shell mobile — `prototipos-mobile/clientes/lista.html`.
 * Só apresentação: busca, filtros, paginação e navegação vêm de `pages/customers/List.js`.
 */

const SlidersIcon = protoIcon('sliders');
const PlusIcon = protoIcon('plus');
const ChevronRight = protoIcon('right');
const UsersIcon = protoIcon('users');

const TYPE_CHIPS = [
    { value: '', label: 'Todos' },
    { value: 'pf', label: 'Pessoa física' },
    { value: 'pj', label: 'Pessoa jurídica' },
];

// `.item` (com `.avatar` no lugar do ícone)
function CustomerRow({ customer, onOpen }) {
    const clickable = Boolean(onOpen);
    return (
        <Box
            component={clickable ? 'a' : 'div'}
            role={clickable ? 'link' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onOpen(customer.ID) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onOpen(customer.ID); } : undefined}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                p: '13px 14px',
                minHeight: 60,
                boxSizing: 'border-box',
                borderBottom: '1px solid',
                borderColor: 'divider',
                color: 'text.primary',
                textDecoration: 'none',
                cursor: clickable ? 'pointer' : 'default',
                '&:last-child': { borderBottom: 0 },
                ...(clickable ? { '&:active': { backgroundColor: 'background.subtle' } } : {}),
            }}
        >
            <KitAvatar size={36} text={customerInitials(customer.Name)} tone={avatarTone(customer.Name, customer.CustomerType)} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{customer.Name || '—'}</Box>
                <Box sx={{ fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: '1px', lineHeight: 1.4 }}>{customerSubtitle(customer)}</Box>
            </Box>
            {clickable && <Box sx={{ color: 'text.disabled', display: 'inline-flex', flexShrink: 0 }}><ChevronRight size={18} /></Box>}
        </Box>
    );
}

export default function MobileCustomersList({
    customers,
    loading,
    totalCount,
    hasMore,
    onLoadMore,
    type,
    onTypeChange,
    search,
    onSearchChange,
    onClearFilters,
    canCreate,
    onNew,
    onOpen,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [draftType, setDraftType] = useState(type);

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitStickyToolbar top={APP_BAR_OFFSET}>
                <KitSearch
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Nome, CPF/CNPJ ou telefone…"
                    inputProps={{ 'aria-label': 'Buscar cliente', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <KitToolbarButton
                    aria-label="Filtros"
                    onClick={() => {
                        setDraftType(type);
                        setFiltersOpen(true);
                    }}
                >
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            </KitStickyToolbar>

            <KitChips>
                {TYPE_CHIPS.map((chip) => (
                    <KitChip
                        key={chip.value || 'all'}
                        active={type === chip.value}
                        count={type === chip.value && !loading ? totalCount : undefined}
                        onClick={() => onTypeChange(chip.value)}
                    >
                        {chip.label}
                    </KitChip>
                ))}
            </KitChips>

            <Box sx={{ mt: '12px' }} />

            {loading && customers.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3, 4].map((n) => <Skeleton key={n} variant="rounded" height={60} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && customers.length === 0 && (
                <KitEmpty icon={UsersIcon} title="Nenhum cliente encontrado">
                    Ajuste a busca ou os filtros para ver outros clientes.
                </KitEmpty>
            )}

            {customers.length > 0 && (
                <KitList>
                    {customers.map((customer) => (
                        <CustomerRow key={customer.ID} customer={customer} onOpen={onOpen} />
                    ))}
                </KitList>
            )}

            {hasMore && (
                <KitButton block sx={{ mt: '14px' }} onClick={onLoadMore} disabled={loading}>
                    {loading ? 'Carregando…' : 'Carregar mais'}
                </KitButton>
            )}

            {canCreate && <KitFab label="Novo" icon={PlusIcon} onClick={onNew} />}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <>
                        <KitButton
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onClearFilters();
                            }}
                        >
                            Limpar
                        </KitButton>
                        <KitButton
                            variant="primary"
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onTypeChange(draftType);
                            }}
                        >
                            Aplicar
                        </KitButton>
                    </>
                )}
            >
                <KitField label="Tipo">
                    <KitSelect value={draftType} onChange={(e) => setDraftType(e.target.value)} aria-label="Tipo">
                        <option value="">Todos</option>
                        <option value="pf">Pessoa física</option>
                        <option value="pj">Pessoa jurídica</option>
                    </KitSelect>
                </KitField>
            </MobileSheet>
        </Box>
    );
}
